import { useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { loadPreparedObjectUrl } from '../../image/crop/cropImageEngine';

/**
 * The crop dialog, shared by every image tool.
 *
 * It hands back a rectangle in PERCENTAGES of the picture, which is the one
 * form that survives being applied to the file later at full resolution. The
 * crop tool keeps that rectangle as state it applies on export; the other tools
 * turn it into a new file straight away.
 *
 * Full screen, and the picture never scrolls: the whole photo has to be in view
 * to crop it well, and a scrolled selection handle is one the user cannot see.
 * The stage measures itself and the image is sized to fit it exactly — a CSS
 * `max-h-full` alone does not hold inside ReactCrop's wrapper, which is how the
 * old dialog ended up capped at a fixed 72vh with a scrollbar.
 */

function defaultPercentCrop(width, height, aspect) {
    if (aspect) {
        return centerCrop(
            makeAspectCrop({ unit: '%', width: 80 }, aspect, width, height),
            width,
            height,
        );
    }
    return centerCrop({ unit: '%', width: 80, height: 80 }, width, height);
}

export default function ImageCropDialog({
    file,
    aspect,
    rotation = 0,
    savedCrop,
    title,
    onClose,
    onSave,
}) {
    const { t } = useTranslation();
    const imgRef = useRef(null);
    const stageRef = useRef(null);
    // Which src already got its starting selection. A ref callback runs on every
    // render, so without this the first crop would be set again and again.
    const startedRef = useRef('');
    const [src, setSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [stage, setStage] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!file) return undefined;
        let cancelled = false;
        let objectUrl = '';

        setSrc('');
        setCrop(undefined);
        setCompletedCrop(null);
        startedRef.current = '';

        (async () => {
            objectUrl = await loadPreparedObjectUrl(file, rotation);
            if (!cancelled) setSrc(objectUrl);
            else URL.revokeObjectURL(objectUrl);
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [file, rotation]);

    // The page behind a full-screen dialog must not scroll along with a drag.
    // Escape closes, like every other full-screen view.
    useEffect(() => {
        if (!file) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previous;
            window.removeEventListener('keydown', onKey);
        };
    }, [file, onClose]);

    // The space the picture may fill, re-measured on resize / rotation of a phone.
    useEffect(() => {
        const node = stageRef.current;
        if (!node) return undefined;
        const measure = () => {
            const { width, height } = node.getBoundingClientRect();
            setStage((prev) => (
                Math.round(prev.width) === Math.round(width) && Math.round(prev.height) === Math.round(height)
                    ? prev
                    : { width, height }
            ));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [file]);

    const startCrop = (node) => {
        if (!src || startedRef.current === src) return;
        // The rendered size can still be 0 when the ref fires; the natural size
        // is what the aspect maths actually needs, and the crop is in percent
        // either way.
        const width = node?.width || node?.naturalWidth;
        const height = node?.height || node?.naturalHeight;
        if (!width || !height) return;
        startedRef.current = src;
        if (savedCrop) {
            setCrop(savedCrop);
            setCompletedCrop(savedCrop);
            return;
        }
        const nextCrop = defaultPercentCrop(width, height, aspect);
        setCrop(nextCrop);
        setCompletedCrop(nextCrop);
    };

    /**
     * An image restored from cache can finish loading before React attaches
     * `onLoad`, which left the dialog with no selection and Save greyed out.
     * The ref catches that case; `onLoad` covers the normal one.
     */
    const attachImage = (node) => {
        imgRef.current = node;
        if (node?.complete && node.naturalWidth) startCrop(node);
    };

    const handleSave = () => {
        if (!completedCrop?.width || !completedCrop?.height) return;
        onSave(completedCrop);
        onClose();
    };

    if (!file) return null;

    // Until the stage has been measured, stay inside it by percentage rather than
    // flashing the picture at its natural size.
    const imageBox = stage.width && stage.height
        ? { maxWidth: `${Math.floor(stage.width)}px`, maxHeight: `${Math.floor(stage.height)}px` }
        : { maxWidth: '100%', maxHeight: '100%' };

    return (
        <div
            className="fixed inset-0 z-[200] flex h-dvh w-screen flex-col overflow-hidden bg-white dark:bg-zinc-900"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex flex-none items-center justify-between gap-3 border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                        {title || t('cropImage.actions.crop')}
                    </p>
                    <p className="truncate text-xs text-[#A8A099] dark:text-zinc-500">{file.name}</p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:text-zinc-300"
                    aria-label={t('common.close')}
                >
                    <IconX size={16} stroke={2} />
                </button>
            </div>

            {/* Dark surround so the picture's own edges read, and no scrolling:
                the inner stage is what the image is fitted to. */}
            <div className="flex min-h-0 flex-1 overflow-hidden bg-[#1C1815] p-3 sm:p-5">
                <div ref={stageRef} className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
                    {src ? (
                        <ReactCrop
                            crop={crop}
                            onChange={(next) => setCrop(next)}
                            onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                            aspect={aspect}
                        >
                            <img
                                ref={attachImage}
                                src={src}
                                alt=""
                                onLoad={(e) => startCrop(e.currentTarget)}
                                style={imageBox}
                                className="block h-auto w-auto object-contain"
                                draggable={false}
                            />
                        </ReactCrop>
                    ) : (
                        <div className="text-sm text-white/70">
                            {t('common.loading')}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                >
                    {t('common.cancel')}
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!completedCrop?.width || !completedCrop?.height}
                >
                    {t('common.save')}
                </button>
            </div>
        </div>
    );
}
